import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Check, MapPin, Truck, Warehouse } from 'lucide-react';
import { validatePermission } from 'actions';
import { cooperplaceApi } from 'services/api';
import { formatDate } from 'v3/utils/controlTowerDate';
import { whatsappContact } from './contract';
import { validateProgress, currentProgress } from './progressContract';
import './progress.css';

const Progress = createContext(null);
export function TransitTimeProvider({ context, rows, children }) {
  const { companyId, group } = context;
  const generation = useRef(0);
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState({ source: null, data: null, loading: true, error: null });
  const [, tick] = useState(0);
  const allowed = validatePermission('VISUALIZAR_VIAGENS');
  const scope = `${companyId}:${group}:${revision}:${allowed}`;
  const refresh = () => setRevision((v) => v + 1);
  useEffect(() => {
    const current = ++generation.current;
    const abort = new AbortController();
    setState({
      scope,
      source: rows,
      loading: allowed && rows.length > 0,
      data: null,
      error: allowed ? null : 'Você não tem permissão para consultar o progresso.',
    });
    if (allowed && rows.length) {
      cooperplaceApi
        .get('control-tower/travel-progress', {
          params: { travelIds: rows.map((r) => r.travelId).join(',') },
          signal: abort.signal,
        })
        .then(({ data }) => {
          const result = validateProgress(data, rows);
          if (!abort.signal.aborted && generation.current === current)
            setState({ scope, source: rows, loading: false, data: result, error: null });
        })
        .catch((error) => {
          if (abort.signal.aborted || generation.current !== current) return;
          const status = error.response?.status;
          const message = [401, 403].includes(status)
            ? 'Você não tem permissão para consultar o progresso.'
            : status === 404
              ? 'Uma viagem mudou ou ficou indisponível. Atualize a lista.'
              : 'Não foi possível consultar o progresso. Tente novamente.';
          setState({ scope, source: rows, loading: false, data: null, error: message });
        });
    }
    return () => abort.abort();
  }, [companyId, group, rows, scope, allowed]);
  // O contrato conserva a captura; expirar a tela não renova a validade da fonte.
  useEffect(() => {
    const limits = (state.data?.data || [])
      .flatMap((r) => [r.position.observation?.validUntil, r.arrival.estimatedAt])
      .map(Date.parse)
      .filter((t) => Number.isFinite(t) && t >= Date.now());
    const delay = Math.min(60000, ...limits.map((t) => Math.max(1, t - Date.now() + 1)));
    const timer = setTimeout(() => tick((v) => v + 1), delay);
    return () => clearTimeout(timer);
  });
  const visible =
    state.scope === scope && state.source === rows
      ? state
      : { loading: true, data: null, error: null };
  return (
    <div className="tc-progress-scope">
      <Progress.Provider value={{ ...visible, refresh, group }}>{children}</Progress.Provider>
    </div>
  );
}
const missing = 'Não informado';
const place = (p) => [p?.city, p?.province].filter(Boolean).join('/') || missing;
const labels = { PICKUP: 'Coleta', TRANSIT: 'Em trânsito', DESTINATION: 'Destino' };
const states = {
  COMPLETED: 'Concluída',
  CURRENT: 'Em andamento',
  PENDING: 'Pendente',
  UNKNOWN: 'Não confirmada',
};
const icons = { PICKUP: Warehouse, TRANSIT: Truck, DESTINATION: MapPin };
function Field({ label, children, title, className = '' }) {
  return (
    <div className={`tc-progress-field ${className}`.trim()}>
      <span>{label}</span>
      <strong title={title}>{children}</strong>
    </div>
  );
}
export default function TransitTime({ item, context, onChanged }) {
  const state = useContext(Progress);
  if (!state) return null;
  const row = state.data?.data.find((r) => r.travelId === item.travelId);
  if (state.loading)
    return (
      <div className="tc-progress" role="status">
        Carregando progresso da viagem…
      </div>
    );
  if (state.error)
    return (
      <div className="tc-progress tc-progress-error" role="alert">
        {state.error}
        <button type="button" onClick={state.refresh}>
          Tentar novamente
        </button>
      </div>
    );
  if (!row) return null;
  const { positionState, arrivalCurrent } = currentProgress(row);
  const p = row.position.observation;
  const positionLabel = {
    CURRENT: 'Posição válida',
    EXPIRED: 'Última posição desatualizada',
    UNVERIFIED: 'Última posição · atualidade não confirmada',
    MISSING: 'Posição não informada',
  }[positionState];
  const positionText = p
    ? [p.information, place(p), formatDate(p.capturedAt) + ' · Brasília']
        .filter(Boolean)
        .join(' · ')
    : 'Sem sinal informado';
  const contact = whatsappContact(row.responsible.phone);
  const cc =
    [row.costCenter.id, row.costCenter.name].filter((v) => v !== null).join(' · ') || missing;
  return (
    <section
      className="tc-progress"
      aria-label={`Progresso da viagem ${item.travelNumber || item.travelId}`}
      data-progress-for={item.travelId}
    >
      {row.group !== context.group && (
        <p className="tc-progress-changed" role="status">
          A viagem mudou de etapa.{' '}
          <button type="button" onClick={onChanged}>
            Atualizar lista
          </button>
        </p>
      )}
      <div className="tc-progress-line">
        <ol className="tc-progress-steps" aria-label="Etapas da viagem">
          {row.stages.map((s) => {
            const Icon = s.state === 'COMPLETED' ? Check : icons[s.code];
            return (
              <li
                key={s.code}
                data-stage={s.code}
                data-state={s.state}
                className={`tc-progress-${s.state.toLowerCase()}`}
                aria-current={s.state === 'CURRENT' ? 'step' : undefined}
                title={`${states[s.state]} · ${s.at ? formatDate(s.at) : s.source === 'TRAVEL_STATUS' ? 'Conforme status da viagem; data não informada' : 'Data não informada'}`}
              >
                <Icon size={18} aria-hidden="true" />
                <span>
                  {labels[s.code]}
                  <small>{states[s.state]}</small>
                </span>
              </li>
            );
          })}
        </ol>
        <Field className="tc-progress-destination" label="Destino" title={place(row.destination)}>
          {place(row.destination)}
        </Field>
        {context.group === 'IN_TRANSIT' && (
          <>
            <Field className="tc-progress-arrival" label="Previsão de chegada">
              {arrivalCurrent ? formatDate(row.arrival.estimatedAt) : 'Previsão não informada'}
            </Field>
            <Field className="tc-progress-distance" label="KM restante">
              {arrivalCurrent
                ? `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 }).format(row.arrival.remainingDistanceMeters / 1000)} km`
                : 'Distância não informada'}
            </Field>
          </>
        )}
        <Field className="tc-progress-position" label={positionLabel} title={positionText}>
          {positionText}
          {p && (
            <small>
              {p.assetType === 'VEHICLE' ? 'Cavalo' : 'Carreta'} ·{' '}
              {p.assetType === 'VEHICLE' ? row.responsible.plate || missing : `#${p.assetId}`}
            </small>
          )}
        </Field>
        <Field
          className="tc-progress-responsible"
          label={`Responsável da placa / filial · ${row.responsible.plate || missing}`}
          title={row.responsible.name || missing}
        >
          {row.responsible.name || missing}
          <small>
            {contact.href ? (
              <a
                href={contact.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`WhatsApp do responsável ${row.responsible.name || ''}`}
              >
                {contact.display}
              </a>
            ) : (
              contact.display || 'Telefone não informado'
            )}
          </small>
        </Field>
        <Field className="tc-progress-cost-center" label="CC" title={cc}>
          {cc}
        </Field>
      </div>
    </section>
  );
}
